import moment from 'moment';
import { format } from 'd3-format';
import { IntlShape } from 'react-intl';

export const formatDate = (
  dateString: string,
  format: string = 'D MMM',
  shouldUpcase: boolean = true,
): string => {
  const formatted = moment(dateString, ['YYYY-MM-DD', 'MMM D']).format(format);
  return shouldUpcase ? formatted.toUpperCase() : formatted;
};

export const formatByOrderOfMagnitude = (count: number): string => {
  if (count === 0) {
    return '0';
  }
  if (Math.abs(count) < 1) {
    return count.toFixed(2);
  }
  if (Math.abs(count) < 10) {
    return count.toFixed(1);
  }
  return format('.2s')(count);
};

export const formatTimestamp = (seconds: number): string => {
  const format = seconds > 3600 ? 'H:mm:ss' : 'mm:ss';
  return moment()
    .startOf('day')
    .seconds(seconds)
    .format(format);
};

export const formatDisplayTimestamp = (seconds: number, intl: IntlShape | null = null): string => {
  let format = '';
  if (seconds > 3600) format += 'h[h]';
  if (seconds > 60) format += ' m[m]';
  if (seconds > 0) format += ' s[s]';
  if (seconds <= 0) return '0s';

  const momentObj = moment()
    .startOf('day')
    .seconds(seconds);

  if (intl) {
    const parts = [
      intl.formatNumber(momentObj.hours(), {
        style: 'unit',
        unit: 'hour',
        unitDisplay: 'narrow',
      }),
      intl.formatNumber(momentObj.minutes(), {
        style: 'unit',
        unit: 'minute',
        unitDisplay: 'narrow',
      }),
      intl.formatNumber(momentObj.seconds(), {
        style: 'unit',
        unit: 'second',
        unitDisplay: 'narrow',
      }),
    ];

    const finalFormat = intl.formatList(parts, { type: 'unit' });
    return finalFormat.split(',').join(' ');
  }

  return momentObj.format(format.trim());
};

export const calculateRawPercentage = (value: number | undefined, total: number): number => {
  if (!value) return 0;
  return (value / total) * 100;
};

export const calculateRoundedPercentage = (value: number | undefined, total: number): number => {
  const percentage = calculateRawPercentage(value, total);
  return percentage < 1 ? percentage : Math.round(percentage);
};

export const formatPercentage = (value: number, intl: IntlShape): string => {
  const rawPercentage = value / 100;
  const isBetweenOneAndZero = value < 1 && value > 0;

  return isBetweenOneAndZero
    ? `<${intl.formatNumber(0.01, { style: 'percent' })}`
    : intl.formatNumber(rawPercentage, {
        style: 'percent',
      });
};

export const commaFormatNumber = (num: number): string => new Intl.NumberFormat().format(num);

// Ensures rounded fractional `values` for a range of data will always add up to 100% of `target`
export const distributeObjectFractals = (
  values: { [key: string]: number },
  target: number,
): { [key: string]: number } => {
  // turn object into array and store its original order
  const valuesArray = Object.entries(values).map(([key, value], index) => ({ key, value, index }));

  // sum up all fractal values
  const remainder =
    target - valuesArray.reduce((previous, current) => previous + Math.round(current.value), 0);

  // if all values are 0, return the original set of values
  if (remainder === target) return values;

  const resultArray = valuesArray
    // order by their fractal value
    .sort((a, b) => Math.round(a.value) - a.value - (Math.round(b.value) - b.value))
    // distribute remainder from biggest to smallest original fractal value
    .map((item, index) => ({
      ...item,
      value:
        Math.round(item.value) +
        (remainder > index ? 1 : 0) -
        (index >= valuesArray.length + remainder ? 1 : 0),
    }))
    // restore original order
    .sort((a, b) => a.index - b.index);

  // put back in original object format
  const result = {};
  resultArray.forEach(item => (result[item.key] = item.value));

  return result;
};

export const truncateStringAtLength = (input: string, maxLength: number) => {
  if (input.length <= maxLength) {
    return input;
  } else {
    return input.slice(0, maxLength) + '...';
  }
};
